import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    LayoutDashboard,
    BarChart3,
    Settings,
    LogOut,
    ChevronLeft,
    Menu,
    ChevronDown,
    Briefcase,
    Zap,
    PlusCircle,
    Activity,
    Star,
    LayoutGrid,
    Target
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useWorkspace, Workspace } from '../hooks/useWorkspace';
import { logout } from '../services/firebase';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { useMediaQuery } from '../hooks/useMediaQuery';

export default function Layout({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { workspaces, activeWorkspace, setActiveWorkspaceId } = useWorkspace();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const location = useLocation();

    const navItems = [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Portfolio', path: '/portfolio', icon: Briefcase },
        { name: 'Trend Explorer', path: '/trends', icon: Activity },
        { name: 'Creative Hub', path: '/creatives', icon: Star },
        { name: 'Forecasting', path: '/forecasting', icon: Target },
        { name: 'Insights & Reports', path: '/reports', icon: BarChart3 },
        { name: 'Connectors', path: '/connections', icon: Zap },
        { name: 'Settings', path: '/settings', icon: Settings },
    ];

    return (
        <div className="min-h-screen bg-dark text-white flex overflow-hidden w-full max-w-[100vw]">
            {/* Mobile Sidebar Overlay */}
            {isMobileSidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setIsMobileSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <motion.aside
                initial={false}
                animate={{
                    width: isSidebarOpen ? 280 : 80,
                    x: isDesktop ? 0 : (isMobileSidebarOpen ? 0 : -280)
                }}
                className="glass border-r border-white/10 flex md:flex flex-col z-50 fixed md:relative h-full"
            >
                {/* Sidebar Header / Logo */}
                <div className="p-6 flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                        <span className="font-serif font-bold text-2xl">K.</span>
                    </div>
                    {isSidebarOpen && (
                        <span className="font-bold text-xl tracking-tight font-serif">Klarvo.</span>
                    )}
                </div>

                {/* Workspace Switcher */}
                <div className="px-4 mb-6">
                    <WorkspaceSwitcher />
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-4 space-y-2">
                    {navItems.map((item) => {
                        const isActive = location.pathname === item.path;
                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`flex items-center gap-4 p-3 rounded-xl transition-all duration-300 ${isActive ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                            >
                                <item.icon size={22} className={isActive ? 'text-primary-foreground' : 'text-gray-400'} />
                                {isSidebarOpen && <span className="font-medium">{item.name}</span>}
                            </Link>
                        );
                    })}
                </nav>

                {/* User / Logout */}
                <div className="p-4 border-t border-white/10">
                    <div className="flex items-center gap-3 mb-4 px-2">
                        <img
                            src={user?.photoURL || 'https://via.placeholder.com/40'}
                            className="w-10 h-10 rounded-full border border-white/10"
                            alt="Profile"
                        />
                        {isSidebarOpen && (
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate text-white">{user?.displayName}</p>
                                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => logout()}
                        className="w-full flex items-center gap-4 p-3 rounded-xl text-red-400 hover:bg-red-400/10 transition-all"
                    >
                        <LogOut size={22} />
                        {isSidebarOpen && <span className="font-medium">Sign Out</span>}
                    </button>
                </div>

                {/* Sidebar Toggle Button */}
                <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-12 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-gray-500 hover:text-white transition-all hover:bg-white/10"
                >
                    <ChevronLeft size={14} className={isSidebarOpen ? '' : 'rotate-180'} />
                </button>
            </motion.aside>

            {/* Main Content */}
            <main className="flex-1 relative overflow-y-auto overflow-x-hidden">
                {/* Mobile Header */}
                <div className="md:hidden glass p-4 flex justify-between items-center sticky top-0 z-40 border-b border-white/10">
                    <button onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}>
                        <Menu size={24} className="text-white" />
                    </button>
                    <span className="font-bold font-serif text-xl">Klarvo.</span>
                    <img
                        src={user?.photoURL || 'https://via.placeholder.com/32'}
                        className="w-8 h-8 rounded-full"
                        alt="Profile"
                    />
                </div>

                <div className="relative z-10 min-h-full">
                    {children}
                </div>

                {/* Background Gradients */}
                <div className="fixed top-0 left-0 w-full h-full pointer-events-none -z-10 bg-dark overflow-hidden">
                    <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[120px]" />
                    <div className="absolute bottom-[20%] right-[10%] w-[30%] h-[30%] bg-accent/20 rounded-full blur-[120px]" />
                </div>
            </main>
        </div>
    );
}
